/**
 * 订单项目打印 HTML 生成 - 用于新窗口打印
 * 使用原生 table 结构，确保打印时正确分页
 */
import numeral from 'numeral'
import i2Math from '@/utils/i2Math'
import { VOUCHER_GIFT_STATUS_OPTIONS, getOptionLabel } from '@/views/order/orderList/config/orderTypeConfig'

const esc = (t) => (t == null || t === '' ? '--' : String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
const money = (v) => (Number.isFinite(Number(v)) ? `¥ ${numeral(v).format('0,0.00')}` : '--')

/** projects 接口按 projectType 1/2/3 分桶；小标题与桶一致（派单权限 type1/2/3 同上），避免首条 projectTypeText 与后续行或与分桶不一致时误导 */
const PROJECT_TYPE_BUCKET_TITLES = {
  1: '护理类',
  2: '客服类',
  3: '其他类'
}

const PRINT_STYLES = `
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 16px; font-family: sans-serif; font-size: 12px; }
  .order-print-block { page-break-after: always; }
  .order-print-block:last-child { page-break-after: auto; }
  .order-title { margin-bottom: 16px; font-size: 16px; font-weight: 600; }
  .section-title { margin-bottom: 8px; font-weight: 500; }
  .section { margin-bottom: 16px; }
  .section-card { margin-bottom: 16px; border: 1px solid #e8e8e8; border-radius: 4px; }
  .section-card-title { padding: 12px 16px; border-bottom: 1px solid #e8e8e8; font-weight: 500; overflow: hidden; }
  .section-card-body { padding: 16px; }
  .section-card-extra { float: right; }
  table { width: 100%; border-collapse: collapse; page-break-inside: auto; }
  thead { display: table-header-group; }
  th, td { border: 1px solid #e8e8e8; padding: 6px 8px; text-align: left; }
  th { background: #fafafa; font-weight: 500; }
  .text-center { text-align: center; }
  .text-right { text-align: right; }
  @media print {
    @page { size: A4; margin: 15mm; }
    body { padding: 0; }
    .print-table { page-break-inside: auto; }
    thead { display: table-header-group; }
  }
</style>
`

function buildTable(columns, dataSource) {
  if (!Array.isArray(dataSource) || dataSource.length === 0) {
    return '<table class="print-table"><thead><tr><th>暂无数据</th></tr></thead><tbody></tbody></table>'
  }
  const headers = columns.map((c) => `<th class="${c.align === 'center' ? 'text-center' : c.align === 'right' ? 'text-right' : ''}" style="${c.width ? `width:${c.width}px` : ''}">${esc(c.title)}</th>`).join('')
  const rows = dataSource.map((row, idx) => {
    const cells = columns.map((col) => {
      let val = col.render ? col.render(row, idx) : row[col.dataIndex]
      if (val == null) val = '--'
      return `<td class="${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : ''}">${typeof val === 'string' ? esc(val) : val}</td>`
    })
    return `<tr>${cells.join('')}</tr>`
  })
  return `<table class="print-table"><thead><tr>${headers}</tr></thead><tbody>${rows.join('')}</tbody></table>`
}

export function buildOrderPrintHtml(data) {
  const {
    orderDetail = {},
    maternityCenterChanKangProjects = {},
    projectArrayObj = {},
    promotionObj = {},
    giftObj = {},
    voucherObj = {}
  } = data

  const givingProjectList = maternityCenterChanKangProjects?.givingProjectList || []
  const projectArray = maternityCenterChanKangProjects?.projectArray || []
  const promotionList = Array.isArray(promotionObj?.data) ? promotionObj.data : []
  const giftList = Array.isArray(giftObj?.data) ? giftObj.data : []
  const voucherList = Array.isArray(voucherObj?.data) ? voucherObj.data : []

  const maternalInfo = orderDetail?.maternalInfo || {}
  const customerName = maternalInfo?.name ?? orderDetail?.customerName ?? orderDetail?.id ?? ''

  const givingProjectTotalMoney = givingProjectList.reduce((sum, r) => {
    const n = Number(r?.projectMoney)
    return i2Math.i2Add(sum, Number.isFinite(n) ? n : 0)
  }, 0)
  const promotionTotalMoney = promotionList.reduce((sum, r) => {
    const n = Number(r?.promotionCost)
    return i2Math.i2Add(sum, Number.isFinite(n) ? n : 0)
  }, 0)
  const giftTotalMoney = giftList.reduce((sum, r) => {
    const cost = Number(r?.giftCost)
    const num = Number(r?.num)
    const itemTotal = i2Math.i2Multiply(Number.isFinite(cost) ? cost : 0, Number.isFinite(num) ? num : 0)
    return i2Math.i2Add(sum, itemTotal)
  }, 0)
  const voucherGiftTotalMoney = voucherList.reduce((sum, r) => {
    const denom = Number(r?.denomination)
    const num = Number(r?.number)
    const itemTotal = i2Math.i2Multiply(Number.isFinite(denom) ? denom : 0, Number.isFinite(num) ? num : 0)
    return i2Math.i2Add(sum, itemTotal)
  }, 0)

  const projectCols = [
    { title: '序号', key: 'index', width: 56, align: 'center', render: (_, idx) => String((idx ?? 0) + 1) },
    { title: '项目名称', dataIndex: 'projectName', render: (r) => esc(r?.projectName) },
    { title: '项目类型', dataIndex: 'typeName', render: (r) => esc(r?.typeName) },
    { title: '购买/可用', render: (r) => `${esc(r?.num)} / ${esc(r?.useNum)}` },
    { title: '执行人', dataIndex: 'actionUserName', render: (r) => esc(r?.actionUserName) },
    { title: '佣金', dataIndex: 'commissionMoney', render: (r) => money(r?.commissionMoney) }
  ]
  const projectColsType = [
    { title: '序号', key: 'index', width: 56, align: 'center', render: (_, idx) => String((idx ?? 0) + 1) },
    { title: '项目名称', dataIndex: 'projectName', render: (r) => esc(r?.projectName) },
    { title: '项目类型', dataIndex: 'typeText', render: (r) => esc(r?.typeText) },
    { title: '购买/可用', render: (r) => `${esc(r?.num)} / ${esc(r?.useNum)}` },
    { title: '执行人', dataIndex: 'actionUserName', render: (r) => esc(r?.actionUserName) },
    { title: '佣金', dataIndex: 'commissionMoney', render: (r) => money(r?.commissionMoney) }
  ]
  const givingCols = [
    { title: '序号', key: 'index', width: 56, align: 'center', render: (_, idx) => String((idx ?? 0) + 1) },
    { title: '项目名称', dataIndex: 'projectName', render: (r) => esc(r?.projectName) },
    { title: '项目类型', dataIndex: 'typeName', render: (r) => esc(r?.typeName) },
    { title: '项目金额', dataIndex: 'projectMoney', align: 'right', render: (r) => money(r?.projectMoney) },
    { title: '门店', dataIndex: 'storeName', width: 80, render: (r) => esc(r?.storeName) },
    { title: '购买/可用', render: (r) => `${esc(r?.num)} / ${esc(r?.useNum)}` },
    { title: '执行人', dataIndex: 'actionUserName', render: (r) => esc(r?.actionUserName) || '-' }
  ]
  const promotionCols = [
    { title: '促销名称', dataIndex: 'promotionName', render: (r) => esc(r?.promotionName) },
    { title: '促销费用', dataIndex: 'promotionCost', render: (r) => money(r?.promotionCost) }
  ]
  const giftCols = [
    { title: '礼品名称', dataIndex: 'giftName', render: (r) => esc(r?.giftName) },
    { title: '费用', dataIndex: 'giftCost', render: (r) => money(r?.giftCost) },
    { title: '数量', dataIndex: 'num', render: (r) => esc(r?.num) }
  ]
  const voucherCols = [
    { title: '代金券名称', dataIndex: 'voucherName', render: (r) => esc(r?.voucherName) },
    { title: '面额', dataIndex: 'denomination', render: (r) => money(r?.denomination) },
    { title: '数量', dataIndex: 'number', render: (r) => esc(r?.number) },
    { title: '套餐名称', dataIndex: 'packageName', render: (r) => esc(r?.packageName) },
    { title: '套餐金额', dataIndex: 'packageMoney', render: (r) => money(r?.packageMoney) },
    { title: '状态', dataIndex: 'status', render: (r) => getOptionLabel(VOUCHER_GIFT_STATUS_OPTIONS, r?.status) }
  ]

  let html = `<div class="order-print-block">
    <div class="order-title">${esc(customerName)} - 订单号：${esc(orderDetail?.id)}</div>

    <div class="section-card">
      <div class="section-card-title">项目明细</div>
      <div class="section-card-body">
        <div class="section">
          <div class="section-title">产康类</div>
          ${buildTable(projectCols, projectArray)}
        </div>`

  ;[projectArrayObj?.type1, projectArrayObj?.type2, projectArrayObj?.type3].forEach((arr, idx) => {
    const list = Array.isArray(arr) ? arr : []
    if (list.length === 0) return
    const bucketNum = idx + 1
    const titleFallback = list[0]?.projectTypeText
    const rawTitle = PROJECT_TYPE_BUCKET_TITLES[bucketNum] || titleFallback || `类型${bucketNum}`
    const title = esc(rawTitle)
    html += `
        <div class="section">
          <div class="section-title">${title}</div>
          ${buildTable(projectColsType, list)}
        </div>`
  })

  html += `
      </div>
    </div>

    <div class="section-card">
      <div class="section-card-title">赠送项目 <span class="section-card-extra">赠送总额：${money(givingProjectTotalMoney)}</span></div>
      <div class="section-card-body">${buildTable(givingCols, givingProjectList)}</div>
    </div>

    <div class="section-card">
      <div class="section-card-title">促销 <span class="section-card-extra">促销总额：${money(promotionTotalMoney)}</span></div>
      <div class="section-card-body">${buildTable(promotionCols, promotionList)}</div>
    </div>

    <div class="section-card">
      <div class="section-card-title">礼品 <span class="section-card-extra">礼品总额：${money(giftTotalMoney)}</span></div>
      <div class="section-card-body">${buildTable(giftCols, giftList)}</div>
    </div>

    <div class="section-card">
      <div class="section-card-title">代金券 <span class="section-card-extra">代金券总额：${money(voucherGiftTotalMoney)}</span></div>
      <div class="section-card-body">${buildTable(voucherCols, voucherList)}</div>
    </div>
  </div>`

  return html
}

export function buildPrintDocumentHtml(ordersData) {
  const blocks = ordersData.map((d) => buildOrderPrintHtml(d))
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>订单打印</title>
  ${PRINT_STYLES}
</head>
<body>
  ${blocks.join('')}
</body>
</html>`
}
